/**
 * A forward-deployed engineer's fortnight with Calder & Wynn, as their
 * Finance channel's Feed tab reads: the systems the engineer connected, the
 * matches the Twin proposed, the definition the team settled, the
 * engineer's check and the word that the workflow is ready to switch on.
 * Newest first, as a feed lists them.
 *
 * Every name is invented for the site (fixture-calder, and the receivables
 * story's customers): the engineer is no one at Mercury Labs, and the firm,
 * its people and its customers are no customer of ours. Each post says only
 * what the site already says: connections start read-only and change
 * nothing in the source systems, the Twin's matches go to the Inbox for a
 * person to accept or decline, the team agrees what a term means, and a
 * result is checked against real examples and stays off until the team
 * switches it on.
 *
 * The posts are written as people write them in the product: they mention
 * teammates and a saved view, link a record and mark a word bold, so the
 * feed draws them with the mentions, pills and type the application draws.
 */

import type { ChannelFeedReaction } from "@mlx/contracts";
import type { CurrentMlxUser, WorkspaceChannel, WorkspaceChannelView, WorkspaceOrganizationMember } from "@product/lib/mlx-workspace-client";
import { Table2Icon } from "lucide-react";

import { CALDER_CHANNELS, calderRecordHref } from "./fixture-calder";
import type { ShotMention } from "./mention-markdown";

/** When an entry happened, as an age from the day the page is seen. */
type Placed = {
  id: string;
  /** Whole days before the day it is seen. */
  daysAgo: number;
  /** Its time that day, hours and minutes, in the viewer's own clock; today's entries are minutes old instead. */
  clock: readonly [hours: number, minutes: number];
  /** For an entry today: how many minutes old it reads. */
  minutesAgo?: number;
  /**
   * Shown only in a frame wide enough to hold the whole feed: narrower
   * rows run longer, and the frame would cut this entry part-way.
   */
  wideOnly?: boolean;
};

/** A post: its author, its text as markdown, and whom and what it mentions. */
export type ShotFeedPost = Placed & {
  kind: "post";
  authorId: string;
  body: string;
  mentions?: readonly ShotMention[];
  replies: number;
  /** Replies the viewer has not read. */
  unreadReplies?: number;
  reactions?: readonly { reaction: ChannelFeedReaction; count: number; byViewer?: boolean }[];
};

/** A session someone ran in the channel, with the saved views it read. */
export type ShotFeedSession = Placed & {
  kind: "session";
  userId: string;
  title: string;
  viewIds: readonly string[];
};

export type ShotFeedEntry = ShotFeedPost | ShotFeedSession;

/** A channel's Feed tab: the channel, the viewer, the people and views its rows name, and its entries, newest first. */
export type ShotChannelFeed = {
  channel: WorkspaceChannel;
  currentUser: CurrentMlxUser;
  directory: WorkspaceOrganizationMember[];
  views: WorkspaceChannelView[];
  entries: readonly ShotFeedEntry[];
};

const MAYA = "usr_maya";
const ENGINEER = "usr_priya";
const TOM = "usr_tom";

const FINANCE = CALDER_CHANNELS.finance;

/** The saved view the engineer built and checked: the receivables, aged. */
const AGED_RECEIVABLES: WorkspaceChannelView = {
  id: "view_aged_receivables",
  channelId: FINANCE.id,
  objectTypeId: "ot_receivables",
  slug: "aged_receivables",
  name: "Aged receivables",
  type: "table",
  status: "active",
  updatedAt: 0,
};

const mention = {
  maya: { type: "teammate", id: MAYA, label: "Maya Hart" },
  priya: { type: "teammate", id: ENGINEER, label: "Priya Anand" },
  tom: { type: "teammate", id: TOM, label: "Tom Reeve" },
  agedReceivables: { type: "view", id: AGED_RECEIVABLES.id, label: AGED_RECEIVABLES.name, channelId: FINANCE.id, channelName: FINANCE.name, icon: Table2Icon },
} as const satisfies Record<string, ShotMention>;

export const ENGINEERING_FEED: ShotChannelFeed = {
  channel: FINANCE,
  currentUser: { id: MAYA, name: "Maya Hart" },
  directory: [
    { id: "mem_maya", userId: MAYA, name: "Maya Hart", isCurrentUser: true },
    { id: "mem_priya", userId: ENGINEER, name: "Priya Anand", isCurrentUser: false },
    { id: "mem_tom", userId: TOM, name: "Tom Reeve", isCurrentUser: false },
  ],
  views: [AGED_RECEIVABLES],
  entries: [
    {
      kind: "post",
      id: "post_ready",
      authorId: ENGINEER,
      body: "@Maya Hart @Aged receivables is ready for you to switch on. It agrees with the three customers @Tom Reeve checked by hand.",
      mentions: [mention.maya, mention.agedReceivables, mention.tom],
      daysAgo: 0,
      clock: [9, 0],
      minutesAgo: 12,
      replies: 2,
      unreadReplies: 1,
      reactions: [{ reaction: "check", count: 2, byViewer: true }],
    },
    {
      kind: "session",
      id: "ses_receivables_check",
      userId: ENGINEER,
      title: "Aged receivables check",
      viewIds: [AGED_RECEIVABLES.id],
      daysAgo: 0,
      clock: [9, 0],
      minutesAgo: 48,
    },
    {
      kind: "post",
      id: "post_overdue",
      authorId: MAYA,
      body: "@Priya Anand overdue should start the day after the due date, not at month end.",
      mentions: [mention.priya],
      daysAgo: 1,
      clock: [16, 20],
      replies: 1,
    },
    {
      kind: "post",
      id: "post_matches",
      authorId: ENGINEER,
      body: `Your Twin found three customers under two names in Xero and HubSpot, [Tarnwick University](${calderRecordHref(FINANCE.id, "rec_tarnwick")}) among them. The matches are in your Inbox with the evidence, to accept or decline.`,
      daysAgo: 1,
      clock: [11, 5],
      replies: 0,
    },
    {
      kind: "post",
      id: "post_connected",
      authorId: ENGINEER,
      body: "Xero, HubSpot and Outlook are connected, **read-only**. Nothing in those systems changes.",
      daysAgo: 8,
      clock: [9, 30],
      replies: 0,
      wideOnly: true,
    },
  ],
};

/** The engineer the feed names, for the figure's caption. */
export const ENGINEERING_ENGINEER = "Priya";

/**
 * When an entry happened, for a page seen at `now`: today's a few minutes
 * ago, an older one at its own time on its own day in the viewer's clock.
 */
export function entryOccurredAt(entry: ShotFeedEntry, now: number): number {
  if (entry.daysAgo === 0) return now - (entry.minutesAgo ?? 0) * 60_000;
  const day = new Date(now);
  day.setDate(day.getDate() - entry.daysAgo);
  day.setHours(entry.clock[0], entry.clock[1], 0, 0);
  return day.getTime();
}
